import { DateHelper, Popup, SchedulerPro, StringHelper } from "@bryntum/schedulerpro";
import { getGridLinesPreferenceSetting, getRowHeight, userPreferences } from "../../../react/context/bryntumContext.jsx";
import { SchedulerCopyDownMixin } from '../mixins/SchedulerCopyDownMixin.jsx';
import {
  disableZoomTimeline,
  getGridRowHeight,
  preventGroupToggleOnGroupHeaderBtn,
  preventMultiselectOnCtrlKeyClick,
  preventRowDeselectOnSameSelectedRowClick,
  removeArchivedRecords
} from "../../../other/utils/utils.jsx";
import SwivuStateMixin from '../mixins/SwivuStateMixin.jsx';
import {GRID_DEFAULT_TEXT} from "../../../other/configs/globalConfig.jsx";
import { createRef } from 'react';
import { TIMELINE_CONG_KEYS } from "../../../other/constants/OtherKeys.jsx";
import { PLUGINS_KEYS } from "../../../other/constants/PluginKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import {
  getIsMajorTick,
  getMajorTickFromMinor,
  isDateRangeInsideLimit,
  isOverlappingOrEqualDateRanges
} from "../../../other/utils/schedulerUtils.jsx";

const canShowPasteConfirmationDialog = (overlappingRecords) => new Promise((resolve, reject) => {
  if (overlappingRecords.length) {
    const popup = new Popup({
      title: "Paste Column",
      centered: true,
      modal: true,
      closeAction: "destroy",
      html: "The paste column operation can split and replace existing task events, or insert additional task events. This operation cannot be undone",
      bbar: {
        contentElementCls: "!pl-[1em] [&_button]:flex-[1_1_auto] [&_button]:!min-w-[auto]",
        items: [
          {
            type: "button",
            text: "Cancel",
            onClick: () => {
              reject();
              popup.close();
            }
          },
          {
            type: "button",
            text: "Insert",
            onClick: () => {
              resolve(true)
              popup.close();
            }
          },
          {
            type: "button",
            text: "Replace",
            cls: "b-blue b-raised",
            onClick: () => {
              resolve(false)
              popup.close();
            }
          }
        ]
      }
    });
  } else {
    resolve(true);
  }
})

export default class SwivuSchedulerPro extends SwivuStateMixin(SchedulerCopyDownMixin(SchedulerPro)) {
  static get $name() {
    return "SwivuSchedulerPro";
  }

  static get type() {
    return "swivuschedulerpro";
  }

  construct() {
    super.construct(...arguments);
    this.keyMap["Delete"] = { handler: "onPressDeleteKey" };
    this.on("cellMouseDown", preventMultiselectOnCtrlKeyClick);
    this.on("beforeSelectionChange", (e) => {
      if (userPreferences) {
        const isSelectionEnabled = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.selectedRowHighlight, false));
        if (isSelectionEnabled) {
          return preventRowDeselectOnSameSelectedRowClick(e)
        }
        return isSelectionEnabled;
      }
    });
    // in bryntum 5.6.10 opening context menu by clicking the group header three dot button, the menu opens at the top left corner of the screen
    // and also the group get collapse/expand (when upgrading to bryntum 6.0 please verify by removing the following code)
    this.on("beforeToggleGroup", preventGroupToggleOnGroupHeaderBtn);
    this.handleSchedulerGlobalSettings();
  }

  static get configurable() {
    return {
      emptyText: GRID_DEFAULT_TEXT,
      features: {
        stripe: { disabled: true }
      },
      getRowHeight: getGridRowHeight
    };
  }

  get swivuSchedulerDefaultState() {
    return {...this.defaultState, features: {...this.defaultState.features, [PLUGINS_KEYS.swivuTimelineConfigurator]: this.features[PLUGINS_KEYS.swivuTimelineConfigurator].defaultState }}
  }

  // TODO: remove after upgrade to 5.2.8+
  // https://github.com/bryntum/support/issues/5814
  get viewportCenterDate() {
    return this.timeAxisViewModel.availableSpace ? super.viewportCenterDate : undefined;
  }

  handleSchedulerGlobalSettings() {
    if (userPreferences) {
      if (this.features.stripe) this.features.stripe.disabled = !(userPreferences.getSetting("stripeFeature") === String(true))
      this.rowLines = getGridLinesPreferenceSetting();
      this.rowHeight = getRowHeight();
      userPreferences.on("change", this.applySchedulerGlobalSettings, this);
    }
  }

  applySchedulerGlobalSettings({ record }) {
    /* Handle stripe feature */
    if (this.features.stripe && record?.key === "stripeFeature") this.features.stripe.disabled = !(record.value === String(true));
    /* Handle row height size based on font size */
    if (["viewType", "fontSize"].includes(record?.key)) this.rowHeight = getRowHeight();
    /* Handle scheduler scroll wheel zoom preference */
    if (record?.key === PERSISTENCE_STATE_KEYS.disableTimelineZoomOnMouse) disableZoomTimeline(this, record.value === "true");
    /* Handle row grid lines */
    if (record?.key === "gridlines") this.rowLines = record.value === String(true);
  }

  applyState(state) {
    // Apply scroll separately due to two issues:
    // https://github.com/bryntum/support/issues/4881
    // https://github.com/bryntum/support/issues/4899
    const {scroll, zoomLevel, zoomLevelOptions, tickSize} = state;

    delete state.scroll;

    // Zooming occurs before applying changes to the subgrids so center date inevitably moves away.
    // Related issue:
    // https://github.com/bryntum/support/issues/5545
    // TODO: Remove zoomLevel-related code when corresponding issue is fixed
    // this code causing the "Update" modal to appear when selecting custom view state of schedule, this happens when
    // switching view from schedule to custom schedule view
  /*  delete state.zoomLevel;
    delete state.zoomLevelOptions;
    delete state.tickSize;
  */
    // Remove barMargin from state
    delete state.barMargin;
    // Ignore event layout, we're using custom one
    delete state.eventLayout;

    // we are saving zoomLevelOptions is global state of view and in view state we are storing tickSize
    // there is an issue with width property in zoomLevelOptions and in viewstate both should be equal otherwise viewstate
    // change gets detected
    if (zoomLevelOptions) zoomLevelOptions.width = tickSize;

    super.applyState(state);

    if (this.isPainted) {
      if (zoomLevel != null) {
        // Raise a special patch flag to make sure width reading ignores cache
        this.timeAxisSubGrid._swivu_skipWidthCache = true;
        this.zoomToLevel(zoomLevel, zoomLevelOptions);
        // Selecting last state tickSize then reapply scroll from state
        this.tickSize = tickSize;
      }
      if (zoomLevelOptions?.centerDate) this.scrollToDate(DateHelper.add(zoomLevelOptions.centerDate, 0, "hour"), { block: "center"})
      this.timelineScroller.scrollBy(-Math.floor(tickSize / 2))
      this.timeAxisSubGrid._swivu_skipWidthCache = false;

    }
    else {
      console.warn("Applying state before paint. This should never be seen in console, but just in case I'm leaving a warning here");
    }

    if (scroll?.scrollTop) {
      // When restoring state pro scheduler will schedule a commit, which will trigger a refresh event
      // which is listened by the view to position rows vertically. This would be the first trigger to
      // restore the vertical scroll
      this.project.on({
        refresh() {
          this.scrollable.y = scroll.scrollTop;
        },
        once: true,
        thisObj: this
      });
    }
  }

  changeCrudManager(crud, was) {
    const result = super.changeCrudManager(crud, was);

    const filterKeys = ["success", "revision", "requestId"];

    let suspendRefresh = false;

    // When scheduler receives `sync` event from crud manager it is going to refresh
    // rows. Sync happens often when we scroll, we want to filter out cases when the
    // only store we sync is the user preferences store
    crud.on({
      beforeSyncApply({ response }) {
        const keys = Object.keys(response).filter(k => !filterKeys.includes(k));
        if (keys.length === 1 && keys[0] === "userpreferences") {
          suspendRefresh = true;

          this.suspendRefresh();
        }
      },
      sync() {
        if (suspendRefresh) {
          suspendRefresh = false;

          this.resumeRefresh();
        }
      },
      prio: -10,
      thisObj: this
    });

    return result;
  }

  onPressDeleteKey() {
    removeArchivedRecords(this.store, this.selectedRecords)
  }

  /**
   * Pushes events further in time to free the date. Will split event if sourceDate is within event duration
   * @param {Date} sourceDate Date to free up
   * @param {ResourceModel} [resource] Target resource. If not provided, entire column is freed
   * @param {String} [shiftUnit="day"] Shift unit
   * @param {Number} [shiftAmount=1] Amount of units to shift
   * @param eventRecord - event record to push
   */

  async pushEventsBy(sourceDate, resource, shiftUnit = "day", shiftAmount = 1, eventRecord, scheduler) {
    const state = this.project.crudStores.map(s => { const state = s.store.reapplyFilterOnUpdate; s.store.reapplyFilterOnUpdate = false; return state; });
    const timelineConfigState = scheduler.features[PLUGINS_KEYS.swivuTimelineConfigurator].state;
    const nonWorkingTimeChecks = timelineConfigState[TIMELINE_CONG_KEYS.nonWorkingTimeChecks];
    const nonWorkingSlots = this.project.nonWorkingTimeStore.query((r) => nonWorkingTimeChecks.includes(r.id));
    const combinedNonWorkingTimes = {};

    function convertDateToMinute(date) {
     return date.getHours() * 60 + date.getMinutes()
    }

    nonWorkingSlots.forEach((a) => {
      let dateObj = {}
      if (a.allDay)
        dateObj = {
          startDate: 0,
          endDate: 1440
        }
        else {
         const beginDate = DateHelper.parse(a.begin, "HH:mm")
         const endDate = DateHelper.parse(a.end, "HH:mm")
          dateObj = {
            startDate: convertDateToMinute(beginDate),
            endDate: convertDateToMinute(endDate),
          }
      }

      const nonWorkingDay = combinedNonWorkingTimes[DateHelper.getDayNames().findIndex(b => b === a.dayName)]
      if (nonWorkingDay) {
        nonWorkingDay.push(dateObj)
        nonWorkingDay.sort((a,b) => a.startDate - b.startDate)
      } else {
        combinedNonWorkingTimes[DateHelper.getDayNames().findIndex(b => b === a.dayName)] = [dateObj]
      }
    })

    let unit = shiftUnit;
    let amount = shiftAmount;
    let eventsToAdd = [];
    let assignmentsToAdd = [];
    const promise = this.project.commitAsync();

    //Todo: Verify the functionality in bryntum 5.5.0 update
    const populateResourceEvents = (r) => {
      const eventsToPush = []
      r.events.forEach(event => {
        if (
          sourceDate < event?.endDate
        ) {
          // Checking if only one event was pushed
          if (eventRecord?.endDate > event.startDate && eventRecord.id !== event.id) return;
          if (sourceDate > event.startDate) {
            const newEvent = this.eventStore.createRecord({
              startDate: DateHelper.clone(sourceDate),
              endDate: DateHelper.clone(event.endDate),
              taskId: event.taskId
            })
            const newAssignment = this.assignmentStore.createRecord({
              resourceId: event.resourceId,
              eventId: newEvent.id
            })
            eventsToAdd.push(newEvent);
            assignmentsToAdd.push(newAssignment);
            event.endDate = DateHelper.clone(sourceDate)
            eventsToPush.push(newEvent)
          } else {
            eventsToPush.push(event)
          }
        }
      })
      return eventsToPush.sort((a, b) => a.startDate - b.startDate);
    }

    (resource ? [resource] : this.resourceStore).forEach((r) => {
      const eventRecords = populateResourceEvents(r)
      for (let eventRecordIndex= 0; eventRecordIndex < eventRecords.length; eventRecordIndex++) {
        let recordsToAdd = [];
        const record = eventRecords[eventRecordIndex];
        if (eventRecordIndex === 0 && sourceDate <= DateHelper.add(record.startDate, shiftAmount * -1, shiftUnit)) {
          break;
        }
        const originalEndDate = DateHelper.clone(record.endDate);
        let lastRecordDateEndDate = null;

        // Skipping push if there are no working days
        const isSkipNonWorking = timelineConfigState[TIMELINE_CONG_KEYS.skipNonWorkingDayDuringReplicating];
        if (isSkipNonWorking && Object.values(combinedNonWorkingTimes).length !== 7) {

          // Creating a record reference to that we can easily update the record.
          const recordRef = createRef();
          recordRef.current = record
          recordRef.current.startDate = DateHelper.add(recordRef.current.startDate, amount, unit)
          recordRef.current.endDate = DateHelper.add(recordRef.current.endDate, amount, unit)

          // Iterator is used to check daywise chunk of large task to verify it that chuck lies in the non working region
          let iterator = DateHelper.clone(recordRef.current.startDate);

          while(iterator < recordRef.current.endDate) {

            // checking if the task is starting from the non working region.
            const currentStartInNonWorkingTime = combinedNonWorkingTimes[recordRef.current.startDate.getDay()];

            // calculating total minutes from the start of the day
            const startDateInMinutes = convertDateToMinute(recordRef.current.startDate);
            // case where task starts from the non working region in this case we don't need to break the task just push it forward
            if (currentStartInNonWorkingTime) {
              const startingTimeInNonWorkingTime = currentStartInNonWorkingTime.find(a => startDateInMinutes >= a.startDate && startDateInMinutes < a.endDate)
              if (startingTimeInNonWorkingTime) {
                const diff = startingTimeInNonWorkingTime.endDate - startDateInMinutes
                recordRef.current.startDate = DateHelper.add(recordRef.current.startDate, diff, "m")
                recordRef.current.endDate = DateHelper.add(recordRef.current.endDate, diff, "m")
                iterator = DateHelper.clone(recordRef.current.startDate);
                continue;
              }
            }


            const currentIteratorInNonWorkingTimes = combinedNonWorkingTimes[iterator.getDay()]
            const iteratorInMinutes = convertDateToMinute(iterator);
            if (currentIteratorInNonWorkingTimes) {
              let nonWorkingRegion = currentIteratorInNonWorkingTimes.find(a => iteratorInMinutes >= a.startDate && iteratorInMinutes < a.endDate)
              if (nonWorkingRegion) {
                const diff = iteratorInMinutes - nonWorkingRegion.startDate;
                const newEndDate = DateHelper.add(DateHelper.clone(recordRef.current.endDate), diff * -1, 'm')
                recordRef.current.endDate = DateHelper.add(iterator, diff, "m")
                iterator = DateHelper.clone(recordRef.current.endDate);
                if (iterator >= newEndDate) continue;
                recordRef.current = {
                  startDate: DateHelper.clone(iterator),
                  endDate: newEndDate,
                }
                recordsToAdd.push(recordRef.current)
                continue;
              }

              const endDateInMinutes = convertDateToMinute(recordRef.current.endDate) || 1440
              nonWorkingRegion = currentIteratorInNonWorkingTimes.find(a => {
                let status = iteratorInMinutes <= a.startDate
                if (status) {
                  if (DateHelper.isEqual(iterator, recordRef.current.endDate, 'd')) {
                    return endDateInMinutes >= a.startDate
                  }
                }
                return status;
              })
              if (nonWorkingRegion) {
                const diff =  nonWorkingRegion.startDate - iteratorInMinutes;
                const newEndDate = DateHelper.add(DateHelper.clone(recordRef.current.endDate), nonWorkingRegion.endDate - nonWorkingRegion.startDate, 'm')
                recordRef.current.endDate = DateHelper.add(iterator, diff, "m")
                iterator = DateHelper.add(recordRef.current.endDate, nonWorkingRegion.endDate - nonWorkingRegion.startDate, 'm');
                if (iterator >= newEndDate) continue;
                recordRef.current = {
                  startDate: DateHelper.clone(iterator),
                  endDate: newEndDate,
                }
                recordsToAdd.push(recordRef.current)
                continue;
              }
            }
            iterator = DateHelper.startOf(DateHelper.add(iterator, 1, "day"), 'd');
          }
          recordsToAdd.forEach((dateRange) => {
            const newEvent = this.eventStore.createRecord({
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              taskId: record.taskId
            })
            const newAssignment = this.assignmentStore.createRecord({
              resourceId: record.resourceId || r.id,
              eventId: newEvent.id
            })
            eventsToAdd.push(newEvent);
            assignmentsToAdd.push(newAssignment);
          });
          lastRecordDateEndDate = DateHelper.clone(recordsToAdd.at(-1)?.endDate || record.endDate);
        } else {
          record.startDate = DateHelper.add(record.startDate, amount, unit);
          record.endDate = DateHelper.add(record.endDate, amount, unit);
          lastRecordDateEndDate = DateHelper.clone(record.endDate);
        }
        let nextEvent = eventRecords[eventRecordIndex + 1];
        if (nextEvent) {
          if (originalEndDate > nextEvent.startDate) continue;
          const diffWithNextEvent = DateHelper.diff(lastRecordDateEndDate, nextEvent.startDate, "minute");
          if (diffWithNextEvent < 0)  {
            amount = Math.abs(diffWithNextEvent);
            unit = "minute";
          } else {
            break;
          }
        }
      }
    })

    this.eventStore.add(eventsToAdd)
    this.assignmentStore.add(assignmentsToAdd)

    // awaiting the promise here since our plan and scheduler event stores are connected and resolving each item
    // independently causes an expensive computation on both stores
    await promise;
    this.project.crudStores.map((s, index) => {
      s.store.reapplyFilterOnUpdate = state[index]
      s.store.filter();
    })
  }

  getEventRecordsByDateRange(dateRange, filterFn = () => true) {
    const { resourceStore } = this;
    const records = [];
    resourceStore.records.forEach((resource) => {
      if (!resource.isGroupHeader) {
        resource.events.forEach((event) => {
          if (
            isDateRangeInsideLimit(
              { startDate: event.startDate, endDate: event.endDate },
              { startDate: dateRange.startDate, endDate: dateRange.endDate }
            ) && filterFn(event, resource)
          ) {
            records.push(event);
          }
        });
      }
    });
    return records;
  }

  onProcessSwivuTimeAxisHeaderMenu({ items, feature, targetElement, point }) {
    const scheduler = feature.owner;
    const { swivuCopiedColumn } = scheduler;
    const isMajorTick = getIsMajorTick(targetElement);
    const tickRange = this.getSwivuTickRange(point, isMajorTick);
    items.pasteColumn.hidden = !(
      swivuCopiedColumn &&
      scheduler.viewPreset.id === swivuCopiedColumn.viewPresetId &&
      isMajorTick === swivuCopiedColumn.isMajorTick &&
      tickRange && !DateHelper.isEqual(tickRange.startDate, swivuCopiedColumn.tickStartDate)
    );
  }

  getSwivuTickRange(point, isMajorTick) {
    const dateRange = {};
    const selectedDate = this.getDateFromCoordinate(point[0], "floor", false);
    if (isMajorTick) {
      const majorTick = getMajorTickFromMinor(this, selectedDate);
      dateRange.startDate = DateHelper.clone(majorTick.start);
      dateRange.endDate = DateHelper.clone(majorTick.end);
    } else {
      const tickIndex = this.timeAxis.getTickFromDate(selectedDate);
      if (tickIndex === -1) return;
      const selectedTick = this.timeAxis.getAt(tickIndex);
      dateRange.startDate = DateHelper.clone(selectedTick.startDate);
      dateRange.endDate = DateHelper.clone(selectedTick.endDate);
    }
    return dateRange;
  }

  async onClickSwivuTimelineCopy({ point, targetElement }) {
    const isMajorTick = getIsMajorTick(targetElement);
    const tickRange = this.getSwivuTickRange(point, isMajorTick);
    if (tickRange) {
      const { startDate: tickStartDate, endDate: tickEndDate } = tickRange
      const copiedDateRanges = this.getEventRecordsByDateRange({
        startDate: tickStartDate,
        endDate: tickEndDate
      }).map((eventRecord) => {
        let startTime = DateHelper.diff(tickStartDate, eventRecord.startDate);
        let endTime = DateHelper.diff(tickStartDate, eventRecord.endDate);
        if (eventRecord.startDate <= tickStartDate) startTime = 0;
        if (eventRecord.endDate >= tickEndDate)
          endTime = DateHelper.diff(tickStartDate, tickEndDate);
        return {
          startTime,
          endTime,
          taskId: eventRecord.taskId,
          resourceId: eventRecord.resourceId
        };
      });
      this.swivuCopiedColumn = {
        copiedDateRanges,
        isMajorTick,
        viewPresetId: this.viewPreset.id,
        tickStartDate,
      }
    }
  }

  async onClickSwivuTimelinePaste({ point, targetElement }) {
    const isPastedMajorTick = getIsMajorTick(targetElement);
    const tickRange = this.getSwivuTickRange(point, isPastedMajorTick);
    if (tickRange) {
      const { startDate: tickStartDate, endDate: tickEndDate } = tickRange;
      const assignmentsToRemove = [],
        assignmentsToAdd = [],
        eventsToRemove = [],
        eventsToAdd = [],
        copiedColumn = this.swivuCopiedColumn,
        state = this.project.crudStores.map((s) => {
          const state = s.store.reapplyFilterOnUpdate;
          s.store.reapplyFilterOnUpdate = false;
          return state;
        }),
        promise = this.project.commitAsync(),
        { assignmentStore, eventStore } = this;
      let pastedColumnRecords = this.getEventRecordsByDateRange({
        startDate: tickStartDate,
        endDate: tickEndDate
      }, ((eventRecord) =>
          copiedColumn.copiedDateRanges.some(
            (copiedDateRange) => {
              const dateRange1 = {
                startDate: DateHelper.add(tickStartDate, copiedDateRange.startTime),
                endDate: DateHelper.add(tickStartDate, copiedDateRange.endTime)
              }
              const dateRange2 = {
                startDate: eventRecord.startDate,
                endDate: eventRecord.endDate,
              }
              return copiedDateRange.resourceId === eventRecord.resourceId && isOverlappingOrEqualDateRanges(dateRange1, dateRange2);
            }
          )
      ));
      // Filtering out records that shouldn't be removed
      canShowPasteConfirmationDialog(pastedColumnRecords).then(async (isInsert) => {
        this.project.beginBatch();
        copiedColumn.copiedDateRanges.forEach((copiedDateRange) => {
          const dateRange1 = {
            startDate: DateHelper.add(tickStartDate, copiedDateRange.startTime),
            endDate: DateHelper.add(tickStartDate, copiedDateRange.endTime)
          }
          const removedEvents = [];
          if (!isInsert) {
            pastedColumnRecords.filter((eventRecord) => {
              if (eventRecord.resourceId === copiedDateRange.resourceId) {
                const dateRange2 = { startDate: eventRecord.startDate, endDate: eventRecord.endDate };
                return isOverlappingOrEqualDateRanges(dateRange1, dateRange2);
              }}).forEach((eventRecord) => {
                const isStartExceeding = DateHelper.isBefore(eventRecord.startDate, dateRange1.startDate);
                const isEndExceeding = DateHelper.isAfter(eventRecord.endDate, dateRange1.endDate);
                if (isStartExceeding && isEndExceeding) {
                  const originalEndDate = DateHelper.clone(eventRecord.endDate);
                  eventRecord.endDate = DateHelper.clone(dateRange1.startDate);
                  const newEvent = eventStore.createRecord({
                    startDate: DateHelper.clone(dateRange1.endDate),
                    endDate: originalEndDate,
                    taskId: eventRecord.taskId,
                  })
                  const newAssignment = assignmentStore.createRecord({
                    resourceId: copiedDateRange.resourceId,
                    eventId: newEvent.id
                  })
                  // Adding this new event due to splitting for copied record(s) comparison
                  pastedColumnRecords.push(newEvent)
                  // Since this new event will be used for overlapping event comparison logic above (See Line 531)
                  // In order to get resourceId from event, we need to add this event and assignment to their store rather than toAdd arrays
                  eventStore.add(newEvent);
                  newEvent.originalData.swivuCopiedFromColumnFeature = true;
                  assignmentStore.add(newAssignment);
                }
                else if (isStartExceeding) {
                  eventRecord.endDate = DateHelper.clone(dateRange1.startDate);
                } else if (isEndExceeding) {
                  const eventEndDate = DateHelper.clone(eventRecord.endDate);
                  eventRecord.startDate = DateHelper.clone(dateRange1.endDate);
                  eventRecord.endDate = eventEndDate;
                } else {
                  assignmentsToRemove.push(assignmentStore.getAssignmentsForEvent(eventRecord)[0]);
                  eventsToRemove.push(eventRecord);
                  removedEvents.push(eventRecord);
                }
              })
            // Excluding records that should not be part of the overlap logic
            pastedColumnRecords = pastedColumnRecords.filter((eventRecord) => (
              !(removedEvents.find((removedEventRecord) => eventRecord.id === removedEventRecord.id))
            ))
          }
          const newEvent = eventStore.createRecord({
            startDate: dateRange1.startDate,
            endDate: dateRange1.endDate,
            taskId: copiedDateRange.taskId
          });
          const newAssignment = assignmentStore.createRecord({
            resourceId: copiedDateRange.resourceId,
            eventId: newEvent.id
          })
          eventsToAdd.push(newEvent);
          assignmentsToAdd.push(newAssignment);
          newEvent.originalData.swivuCopiedFromColumnFeature = true;
        });

        eventStore.remove(eventsToRemove);
        eventStore.add(eventsToAdd);
        assignmentStore.remove(assignmentsToRemove);
        assignmentStore.add(assignmentsToAdd);
        this.project.endBatch();
        await promise;
        this.project.crudStores.map((s, index) => {
          s.store.reapplyFilterOnUpdate = state[index];
          s.store.filter();
        });
      }).catch(() => {})

    }
  }

  /* Todo: Refactor the swivu copy column splitting using this this splitEvents function */

  /**
   * Splits overlapping or partially overlapping events within a specified date range.
   *
   * This function processes events related to a specific resource and date range. It identifies
   * events that need to be split, removed, or adjusted if they are falling into the specified dateRange, and optionally applies the changes to
   * the event and assignment stores if `canPersist` is set to `true`.
   *
   * @param {Object} params - The parameters object.
   * @param {{ startDate: Date, endDate: Date }} params.dateRange - The date range to split events by.
   * @param {string|number} params.resourceId - The ID of the resource whose events are being split.
   * @param {Array<Record>} [params.filteredEvents] - Optional array of pre-filtered events to use instead of querying the store.
   * @param {Array<string|number>} [params.excludeEvents=[]] - Array of event IDs to exclude from processing.
   * @param {boolean} [params.canPersist=false] - Whether to persist the changes directly to the stores.
   *
   * @returns {{
   *   eventsToAdd: Record[],
   *   eventsToRemove: Record[],
   *   assignmentsToAdd: Record[],
   *   assignmentsToRemove: Record[],
   *   changesToEvents: Array<{ event: Record, changes: { startDate?: Date, endDate?: Date } }>
   * }} Result of the operation.
   *
   * @example
   * const result = splitEvents({
   *   dateRange: { startDate: event.startDate, endDate: event.endDate },
   *   resourceId: 1,
   *   excludeEvents: [1, 'generated_phantomId-41234'],
   *   canPersist: true
   * });
   */
  splitEvents({ dateRange, resourceId, filteredEvents, excludeEvents = [], canPersist = false }) {
    const { resourceStore, assignmentStore, eventStore } = this;

    const eventsToAdd = [];
    const eventsToRemove = [];
    const assignmentsToAdd = [];
    const assignmentsToRemove = [];
    const changesToEvents = [];

    (filteredEvents || resourceStore.getById(resourceId).events.filter(event =>
      isOverlappingOrEqualDateRanges(dateRange, { startDate: event.startDate, endDate: event.endDate }) &&
      !excludeEvents.includes(event.id)
    )).forEach(event => {
      const isStartExceeding = DateHelper.isBefore(event.startDate, dateRange.startDate);
      const isEndExceeding = DateHelper.isAfter(event.endDate, dateRange.endDate);
      if (isStartExceeding && isEndExceeding) {
        const originalEndDate = DateHelper.clone(event.endDate);
        changesToEvents.push({
          event,
          changes: { endDate: DateHelper.clone(dateRange.startDate) }
        });

        const newEvent = eventStore.createRecord({
          startDate: DateHelper.clone(dateRange.endDate),
          endDate: originalEndDate,
          taskId: event.taskId,
        });

        const newAssignment = assignmentStore.createRecord({
          resourceId: event.resourceId,
          eventId: newEvent.id
        });

        eventsToAdd.push(newEvent);
        assignmentsToAdd.push(newAssignment);
      } else if (isStartExceeding) {
        changesToEvents.push({
          event,
          changes: { endDate: DateHelper.clone(dateRange.startDate) }
        });
      } else if (isEndExceeding) {
        const eventEndDate = DateHelper.clone(event.endDate);
        changesToEvents.push({
          event,
          changes: { startDate: DateHelper.clone(dateRange.endDate), endDate: eventEndDate }
        })
      } else {
        assignmentsToRemove.push(assignmentStore.getAssignmentsForEvent(event)[0]);
        eventsToRemove.push(event);
      }
    });

    if (canPersist) {
      this.project.beginBatch();
      changesToEvents.forEach(({ event, changes }) => {
        Object.entries(changes).forEach(([key, value]) => event[key] = value);
      });
      eventStore.remove(eventsToRemove);
      eventStore.add(eventsToAdd);
      assignmentStore.remove(assignmentsToRemove);
      assignmentStore.add(assignmentsToAdd);
      this.project.endBatch();
    }

    return { eventsToAdd, eventsToRemove, assignmentsToAdd, assignmentsToRemove, changesToEvents };
  }
}

SwivuSchedulerPro.initClass();
